import React, { useMemo, useState } from 'react';
import Modal from './Modal';
import { useAllFootage, useTrips } from '../data/firestore';
import { httpsify } from '../utils/url';
import { assetRealSpeedX } from '../utils/movie';

// Short label for a slow-mo source's real-motion speed-up (e.g. "6.67× slow-mo").
const slomoLabel = (a) => {
  const x = assetRealSpeedX(a);
  return x > 0 ? `${x}× slow-mo` : '';
};

const fmtBytes = (n) => {
  if (!n) return '';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

// Pick uploaded trip footage. multi=true lets the user select several clips at
// once (onPick receives an array); otherwise onPick receives a single asset.
export default function FootagePickerModal({ onClose, onPick, multi = false }) {
  // Videos only, filtered before the recency cap — otherwise frame-grab photo
  // assets (hundreds per proof session) push every clip out of the window.
  const { docs: footage } = useAllFootage(300, 'video');
  const { docs: trips } = useTrips();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState({}); // `${tripId}/${id}` -> asset
  const [view, setView] = useState('thumb'); // 'thumb' | 'list'

  const tripName = useMemo(() => {
    const m = {};
    (trips || []).forEach((t) => {
      m[t.id] = t.name || 'Untitled trip';
    });
    return m;
  }, [trips]);

  // R3D clips are usable as a source once their HLS proxy is ready.
  const isReady = (a) => !a.isRawVideo || a.proxy?.status === 'ready';

  const groups = useMemo(() => {
    const vids = (footage || [])
      .filter((a) => a.kind !== 'photo' && (a.s3Key || a.proxyHlsUrl))
      .filter((a) => (a.name || '').toLowerCase().includes(q.trim().toLowerCase()));
    const byTrip = {};
    vids.forEach((a) => {
      (byTrip[a.tripId] = byTrip[a.tripId] || []).push(a);
    });
    return Object.entries(byTrip).sort((a, b) =>
      (tripName[a[0]] || '').localeCompare(tripName[b[0]] || '')
    );
  }, [footage, q, tripName]);

  const selCount = Object.keys(sel).length;
  const keyOf = (a) => `${a.tripId}/${a.id}`;
  const toggle = (a) =>
    setSel((prev) => {
      const k = keyOf(a);
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = a;
      return next;
    });

  const addSelected = () => {
    onPick(Object.values(sel));
  };

  return (
    <Modal
      title={multi ? 'Add clips from your trips' : 'Use uploaded footage as the video'}
      onClose={onClose}
      maxWidth={620}
    >
      <div className="cs-pick-toolbar">
        <input
          className="cs-input"
          placeholder="Search footage…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="cs-segment cs-segment-sm" role="group" aria-label="View">
          <button className={view === 'thumb' ? 'on' : ''} onClick={() => setView('thumb')} title="Thumbnail grid">⊞ Thumbnails</button>
          <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')} title="Compact list">☰ List</button>
        </div>
      </div>
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {footage === null ? (
          <div>Loading…</div>
        ) : groups.length === 0 ? (
          <div className="cs-empty">No uploaded footage found.</div>
        ) : (
          groups.map(([tripId, assets]) => (
            <div key={tripId} style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'var(--surf)',
                  margin: '6px 0',
                }}
              >
                {tripName[tripId] || 'Trip'}
              </div>
              {view === 'thumb' ? (
                <div className="cs-pickgrid">
                  {assets.map((a) => {
                    const selected = !!sel[keyOf(a)];
                    const isRed = !!a.isRawVideo;
                    const ready = isReady(a);
                    const proxyStatus = a.proxy?.status;
                    const thumb = a.thumbUrl ? httpsify(a.thumbUrl) : '';
                    const slomo = slomoLabel(a);
                    return (
                      <button
                        type="button"
                        key={keyOf(a)}
                        className={`cs-pickcard${selected ? ' on' : ''}`}
                        disabled={!ready}
                        title={a.name}
                        onClick={() => (!ready ? null : multi ? toggle(a) : onPick(a))}
                      >
                        <div
                          className="cs-pickcard-thumb"
                          style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                        >
                          {!thumb && <span className="cs-pickcard-ph">{isRed ? '🎞' : '🎬'}</span>}
                          {isRed && <span className="cs-pickcard-red">RED</span>}
                          {slomo && <span className="cs-pickcard-slomo" title={`Slow motion — speed up ${slomo} to reach real-life motion`}>⏱ {slomo}</span>}
                          {selected && <span className="cs-pickcard-check">✓</span>}
                          {!ready && (
                            <span className="cs-pickcard-proxy">
                              {proxyStatus === 'error' ? 'proxy failed' : `building proxy${a.proxy?.progress ? ` ${a.proxy.progress}%` : '…'}`}
                            </span>
                          )}
                        </div>
                        <div className="cs-pickcard-name">{a.name}</div>
                        <div className="cs-pickcard-sub">{[fmtBytes(a.sizeBytes), slomo].filter(Boolean).join(' · ')}</div>
                      </button>
                    );
                  })}
                </div>
              ) : (
              assets.map((a) => {
                const selected = !!sel[keyOf(a)];
                const isRed = !!a.isRawVideo;
                const ready = isReady(a);
                const proxyStatus = a.proxy?.status;
                return (
                  <div
                    key={keyOf(a)}
                    onClick={() => (!ready ? null : multi ? toggle(a) : onPick(a))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 8px',
                      borderRadius: 8,
                      cursor: ready ? 'pointer' : 'not-allowed',
                      opacity: ready ? 1 : 0.5,
                      background: selected ? 'rgba(20,224,200,0.12)' : 'transparent',
                      borderBottom: '1px solid #1e1e26',
                    }}
                  >
                    {multi && (
                      <input
                        type="checkbox"
                        checked={selected}
                        readOnly
                        disabled={!ready}
                        style={{ pointerEvents: 'none' }}
                      />
                    )}
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div
                        style={{
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {isRed ? '🎞' : '🎬'} {a.name}
                        {isRed && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              fontWeight: 800,
                              color: '#fff',
                              background: '#e0241c',
                              padding: '1px 5px',
                              borderRadius: 4,
                              verticalAlign: 'middle',
                            }}
                          >
                            RED
                          </span>
                        )}
                      </div>
                      <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                        {fmtBytes(a.sizeBytes)}
                        {slomoLabel(a) && (
                          <span style={{ color: '#f5c451' }} title="Slow motion — speed up to reach real-life motion">
                            {' · ⏱ '}{slomoLabel(a)}
                          </span>
                        )}
                        {isRed && !ready && (
                          <span style={{ color: 'var(--surf)' }}>
                            {' · '}
                            {proxyStatus === 'error'
                              ? 'proxy failed'
                              : `building proxy${a.proxy?.progress ? ` ${a.proxy.progress}%` : '…'}`}
                          </span>
                        )}
                      </div>
                    </div>
                    {!multi && (
                      <button
                        className="cs-btn cs-btn-primary cs-btn-sm"
                        disabled={!ready}
                        onClick={() => ready && onPick(a)}
                      >
                        Use this
                      </button>
                    )}
                  </div>
                );
              })
              )}
            </div>
          ))
        )}
      </div>
      {multi && (
        <div className="cs-wizard-foot">
          <span className="cs-time">{selCount} selected</span>
          <button className="cs-btn cs-btn-primary" disabled={!selCount} onClick={addSelected}>
            Add {selCount || ''} clip{selCount === 1 ? '' : 's'}
          </button>
        </div>
      )}
    </Modal>
  );
}
